import { useState } from 'react';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useDateLocale } from '../../../utils/dateLocale';
import { Icon, Pencil, Trash2, Bell, BedDouble, MapPin, ChevronDown, Paperclip } from '../../ui/Icon';
import { fileIcon } from './documentFiles';
import { TicketExpenseActions, TicketExpenseBadge } from './TicketExpenses';
import { hotelOption } from '../../../utils/reservations';
import type { Hotel, HotelDocument } from '../../../types';

const R2_PUBLIC_URL = (import.meta.env.VITE_R2_PUBLIC_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const fileUrl = (filename: string) => `${R2_PUBLIC_URL}/${filename}`;

interface Props {
  hotel: Hotel;
  docs?: HotelDocument[];
  onEdit: () => void;
  onDelete: () => void;
}

export function StayTicket({ hotel, docs = [], onEdit, onDelete }: Props) {
  const dateLocale = useDateLocale();
  const reservation = hotelOption(hotel);
  const [open, setOpen] = useState(false);
  const nights = differenceInDays(parseISO(hotel.checkOut.slice(0, 10)), parseISO(hotel.checkIn.slice(0, 10)));

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-card hover:shadow-hero hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-150">

      {/* ── Mobile header ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="md:hidden w-full text-left flex items-stretch cursor-pointer"
      >
        {/* Icon strip */}
        <div className="bg-amber-50/70 dark:bg-amber-900/15 flex flex-col items-center justify-center px-2 py-3 gap-1.5 shrink-0 w-12">
          <Icon as={BedDouble} size={18} className="text-amber-500 dark:text-amber-400" />
          <span className="font-mono text-[9px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            stay
          </span>
        </div>

        {/* Main content */}
        <div className="flex-1 px-4 py-3.5 min-w-0">
          <p className="font-semibold text-base text-slate-900 dark:text-slate-100 truncate leading-tight">{hotel.name}</p>

          {/* Check-in → Check-out */}
          <div className="flex items-center gap-2 mt-2">
            <span className="font-serif italic font-bold text-lg text-slate-800 dark:text-slate-200"
                  style={{  }}>
              {format(parseISO(hotel.checkIn.slice(0, 10)), 'dd MMM', { locale: dateLocale })}
            </span>
            <span className="font-mono text-[11px] text-slate-400">→</span>
            <span className="font-serif italic font-bold text-lg text-slate-800 dark:text-slate-200"
                  style={{  }}>
              {format(parseISO(hotel.checkOut.slice(0, 10)), 'dd MMM', { locale: dateLocale })}
            </span>
            <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold ml-1">
              · {nights} {nights === 1 ? 'night' : 'nights'}
            </span>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-2.5 mt-2 flex-wrap">
            {hotel.address && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[180px]">
                <Icon as={MapPin} size={10} />{hotel.address}
              </span>
            )}

            {hotel.reminderMinutes && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
                <Icon as={Bell} size={10} />
                {hotel.reminderMinutes >= 60 ? `${hotel.reminderMinutes / 60}h` : `${hotel.reminderMinutes}min`}
              </span>
            )}
            {docs.length > 0 && (
              <span className="flex items-center gap-1 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                <Icon as={Paperclip} size={10} /> {docs.length}
              </span>
            )}
            <TicketExpenseBadge link={reservation.link} />
          </div>
        </div>

        {/* Chevron */}
        <div className="flex items-center px-3">
          <Icon
            as={ChevronDown}
            size={16}
            className={`text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* ── Desktop header ── */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="hidden md:grid w-full text-left md:grid-cols-[56px_1fr_104px_1fr_1.2fr] divide-x divide-dashed divide-slate-200 dark:divide-slate-700 cursor-pointer"
      >
        {/* Col 1: Type */}
        <div className="bg-amber-50/70 dark:bg-amber-900/15 flex flex-col items-center justify-center px-2 py-4 gap-2">
          <Icon as={BedDouble} size={20} className="text-amber-500 dark:text-amber-400" />
          <span
            className="font-mono text-[10px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-semibold"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            stay
          </span>
        </div>

        {/* Col 2: Check-in */}
        <div className="flex flex-col justify-center px-5 py-5">
          <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 mb-2">Check-in</span>
          <span className="font-serif italic font-bold text-[28px] leading-tight text-slate-900 dark:text-slate-100"
                style={{  }}>
            {format(parseISO(hotel.checkIn.slice(0, 10)), 'dd MMM', { locale: dateLocale })}
          </span>
          <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 tabular-nums">
            {format(parseISO(hotel.checkIn.slice(0, 10)), 'EEEE · yyyy', { locale: dateLocale })}
          </span>
        </div>

        {/* Col 3: Nights */}
        <div className="flex flex-col items-center justify-center px-2 py-5 gap-1">
          <span className="font-serif italic font-bold text-[40px] leading-none text-amber-600 dark:text-amber-400 tabular-nums"
                style={{  }}>
            {nights}
          </span>
          <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest font-semibold">
            {nights === 1 ? 'night' : 'nights'}
          </span>
          <div className="flex items-center gap-1 mt-1.5">
            {Array.from({ length: Math.min(nights, 7) }).map((_, i) => (
              <span key={i} className="w-1 h-1 rounded-full bg-amber-400 dark:bg-amber-500" />
            ))}
            {nights > 7 && <span className="font-mono text-[8px] text-slate-400 ml-0.5">+</span>}
          </div>
        </div>

        {/* Col 4: Check-out */}
        <div className="flex flex-col justify-center px-5 py-5">
          <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 mb-2">Check-out</span>
          <span className="font-serif italic font-bold text-[28px] leading-tight text-slate-900 dark:text-slate-100"
                style={{  }}>
            {format(parseISO(hotel.checkOut.slice(0, 10)), 'dd MMM', { locale: dateLocale })}
          </span>
          <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-1.5 tabular-nums">
            {format(parseISO(hotel.checkOut.slice(0, 10)), 'EEEE · yyyy', { locale: dateLocale })}
          </span>
        </div>

        {/* Col 5: Hotel info + chevron */}
        <div className="flex flex-col justify-center px-5 py-5 gap-2 min-w-0">
          <p className="font-semibold text-base text-slate-800 dark:text-slate-200 truncate leading-tight">{hotel.name}</p>
          {hotel.address && (
            <span className="flex items-center gap-1.5 font-mono text-[12px] text-slate-500 dark:text-slate-400 truncate">
              <Icon as={MapPin} size={12} className="shrink-0" />
              {hotel.address}
            </span>
          )}
          {hotel.reminderMinutes && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-amber-600 dark:text-amber-400 font-semibold">
              <Icon as={Bell} size={11} />
              {hotel.reminderMinutes >= 60 ? `${hotel.reminderMinutes / 60}h` : `${hotel.reminderMinutes}min`}
            </span>
          )}
          {docs.length > 0 && (
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
              <Icon as={Paperclip} size={11} /> {docs.length} {docs.length === 1 ? 'doc' : 'docs'}
            </span>
          )}
          <TicketExpenseBadge link={reservation.link} size={11} />
          <Icon
            as={ChevronDown}
            size={16}
            className={`mt-auto text-slate-300 dark:text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* ── Expanded content ── */}
      {open && (
        <>
          {docs.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-800/30 px-4 py-2 flex items-center gap-2 flex-wrap">
              <Icon as={Paperclip} size={11} className="text-slate-400 dark:text-slate-500 shrink-0" />
              {docs.map(doc => (
                <a
                  key={doc.id}
                  href={fileUrl(doc.filename)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[10px] font-medium text-teal-600 dark:text-teal-400 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors max-w-[180px]"
                  title={doc.originalName}
                >
                  <Icon as={fileIcon(doc.mimeType)} size={10} className="shrink-0" />
                  <span className="truncate">{doc.originalName}</span>
                </a>
              ))}
            </div>
          )}

          {hotel.notes && (
            <div className="border-t border-dashed border-slate-200 dark:border-slate-700 bg-stone-50/60 dark:bg-slate-800/40 px-4 py-3">
              <p className="font-mono text-[11px] text-slate-500 dark:text-slate-400 whitespace-pre-wrap break-words">{hotel.notes}</p>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2 flex items-center justify-end gap-1">
            <TicketExpenseActions reservation={reservation} />
            <button
              onClick={e => { e.stopPropagation(); onEdit(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors cursor-pointer"
            >
              <Icon as={Pencil} size={12} /> Editar
            </button>
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors cursor-pointer"
            >
              <Icon as={Trash2} size={12} /> Eliminar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
